import React from 'react';
import classNames from 'classnames';
import EmptyStateBase from '@alltrails/shared/components/EmptyStateBase';
import PrimaryButtonLarge from '@alltrails/shared/components/PrimaryButtonLarge';
import { PageStrings } from '@alltrails/shared/utils/pageStringHelpers';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import * as styles from './styles/styles.module.scss';
import NoResultsImage from '../../../../assets/images/icons/no-results-found-pin.svg';

export type Props = {
  atMaps: any[];
  cta: any;
  hasActiveFilters: boolean;
  initialNoResultsTitle: string;
  onClick: (pageKey: string) => void;
  page: string;
  resultCount: number;
  subText: string;
  trailIds: string[];
}

enum Icon {
  FAVORITE,
  RECORDINGS,
  TRAILPOST
}

const iconToStyles = {
  [Icon.FAVORITE]: styles.favorite,
  [Icon.RECORDINGS]: styles.recordings,
  [Icon.TRAILPOST]: styles.trailpost
};

const getNoResultsDiv = (header: string, iconText: string, iconClass = Icon.TRAILPOST) => (
  <div className={styles.searching}>
    <div className={styles.header}>{header}</div>
    <div className={classNames(styles.icon, iconToStyles[iconClass])}>
      <span className={styles.iconText}>{iconText}</span>
    </div>
  </div>
);

const NoResults = ({ atMaps, hasActiveFilters, onClick, initialNoResultsTitle, page, resultCount, trailIds }: Props) => {
  let noResultsDiv = getNoResultsDiv(StringConstantsMixin.strings.SEARCH.SEARCH_FOR_TRAILS, StringConstantsMixin.strings.SEARCH.TRAIL_LOAD);

  switch (page) {
    case PageStrings.EXPLORE_ALL_PAGE:
    case PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE:
      let stringsPath;

      let tabToPage = page === PageStrings.EXPLORE_ALL_PAGE ? PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE : PageStrings.EXPLORE_ALL_PAGE;

      if (hasActiveFilters) {
        if (page === PageStrings.EXPLORE_ALL_PAGE) {
          stringsPath = StringConstantsMixin.strings.SEARCH.NO_RESULTS.CURATED_TRAILS_OVER_FILTERED;
        } else {
          stringsPath = StringConstantsMixin.strings.SEARCH.NO_RESULTS.COMMUNITY_CONTENT_OVER_FILTERED;
        }
      } else {
        if (page === PageStrings.EXPLORE_ALL_PAGE) {
          stringsPath = StringConstantsMixin.strings.SEARCH.NO_RESULTS.CURATED_TRAILS;
        } else {
          stringsPath = StringConstantsMixin.strings.SEARCH.NO_RESULTS.COMMUNITY_CONTENT;
        }
      }

      const text = {
        title: {
          long: StringConstantsMixin.strings.SEARCH.NO_RESULTS_FOUND,
          short: stringsPath.TITLE.SHORT
        },
        body: {
          long: stringsPath.BODY.LONG,
          short: stringsPath.BODY.SHORT
        }
      };
      noResultsDiv = (
        <EmptyStateBase
          className={styles.noResults}
          cta={(
            <div className={styles.noResultsButton}>
              <PrimaryButtonLarge name={stringsPath.BUTTON} onClick={() => onClick(tabToPage)}>
                {stringsPath.BUTTON}
              </PrimaryButtonLarge>
            </div>
          )}
          icon={<img src={NoResultsImage} />}
          subText={text.body[resultCount < 1 ? 'long' : 'short']}
          title={text.title[resultCount < 1 ? 'long' : 'short']}
        />
      );
      break;
    case PageStrings.EXPLORE_FAVORITE_PAGE:
      if (!trailIds || trailIds.length === 0) {
        noResultsDiv = getNoResultsDiv(initialNoResultsTitle, StringConstantsMixin.strings.SEARCH.FAVORITE_PROMPT, Icon.FAVORITE);
      } else if (resultCount === 0) {
        noResultsDiv = getNoResultsDiv(
          StringConstantsMixin.strings.NO_RESULTS,
          StringConstantsMixin.strings.SEARCH.CLEAR_FILTERS_MORE_RESULTS,
          Icon.FAVORITE
        );
      }
      break;
    case PageStrings.EXPLORE_COMPLETED_PAGE:
      if (!trailIds || trailIds.length === 0) {
        noResultsDiv = getNoResultsDiv(initialNoResultsTitle, StringConstantsMixin.strings.SEARCH.COMPLETION_PROMPT);
      } else if (resultCount === 0) {
        noResultsDiv = getNoResultsDiv(StringConstantsMixin.strings.NO_RESULTS, StringConstantsMixin.strings.SEARCH.CLEAR_FILTERS_MORE_RESULTS);
      }
      break;
    case PageStrings.EXPLORE_CUSTOM_PAGE:
      if (!trailIds || trailIds.length === 0) {
        noResultsDiv = getNoResultsDiv(StringConstantsMixin.strings.NO_TRAILS_IN_LIST, StringConstantsMixin.strings.SEARCH.LIST_PROMPT);
      } else if (resultCount === 0) {
        noResultsDiv = getNoResultsDiv(StringConstantsMixin.strings.NO_RESULTS, StringConstantsMixin.strings.SEARCH.CLEAR_FILTERS_MORE_RESULTS);
      }
      break;
    case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      if (!atMaps) {
        noResultsDiv = getNoResultsDiv(
          StringConstantsMixin.strings.SEARCH.RECORDING_LOAD_YOUR,
          StringConstantsMixin.strings.SEARCH.RECORDING_LOAD,
          Icon.RECORDINGS
        );
      } else if (atMaps && atMaps.length === 0) {
        noResultsDiv = getNoResultsDiv(initialNoResultsTitle, StringConstantsMixin.strings.SEARCH.RECORDER_PROMPT, Icon.RECORDINGS);
      } else if (resultCount === 0) {
        noResultsDiv = getNoResultsDiv(
          StringConstantsMixin.strings.NO_RECORDINGS,
          StringConstantsMixin.strings.SEARCH.CLEAR_FILTERS_ALL_RECORDINGS,
          Icon.RECORDINGS
        );
      }
      break;
    case PageStrings.EXPLORE_USERS_MAPS_PAGE:
      if (!atMaps) {
        noResultsDiv = getNoResultsDiv(StringConstantsMixin.strings.SEARCH.MAP_LOAD_YOUR, StringConstantsMixin.strings.SEARCH.MAP_LOAD);
      } else if (atMaps && atMaps.length === 0) {
        noResultsDiv = getNoResultsDiv(initialNoResultsTitle, StringConstantsMixin.strings.SEARCH.PLAN_PROMPT);
      } else if (resultCount === 0) {
        noResultsDiv = getNoResultsDiv(StringConstantsMixin.strings.NO_MAPS, StringConstantsMixin.strings.SEARCH.CLEAR_FILTERS_ALL_MAPS);
      }
      break;
  }

  return noResultsDiv;
};

NoResults.defaultProps = {
  handler: () => {},
  title: '',
  text: '',
  buttonText: '',
  className: ''
};

export default NoResults;
